import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { gsap } from '../lib/gsapSetup';
import './popupad.css';

const LOCAL_KEY = 'techne_popup_seen';

const fmt = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const useCountdown = (targetIso) => {
  const calc = () => {
    const diff = new Date(targetIso) - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0, expired: true };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      mins: Math.floor((diff % 3600000) / 60000),
      secs: Math.floor((diff % 60000) / 1000),
      expired: false
    };
  };

  const [tick, setTick] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTick(calc()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return tick;
};

const PopupAd = () => {
  const [visible, setVisible] = useState(false);
  const [event, setEvent] = useState(null);
  const backdropRef = useRef(null);
  const cardRef = useRef(null);

  const countdown = useCountdown(event?.booking_end_date);

  useEffect(() => {
    if (localStorage.getItem(LOCAL_KEY)) return;
    supabase.from('event_settings').select('*').limit(1).single().then(({ data }) => setEvent(data));
  }, []);

  useEffect(() => {
    if (!event) return;
    const id = setTimeout(() => setVisible(true), 12000);
    return () => clearTimeout(id);
  }, [event]);

  useEffect(() => {
    if (!visible) return;
    gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.35 });
    gsap.fromTo(cardRef.current, { scale: 0.88, opacity: 0, y: 30 }, { scale: 1, opacity: 1, y: 0, duration: 0.5, ease: 'back.out(1.6)', delay: 0.05 });
  }, [visible]);

  const dismiss = () => {
    localStorage.setItem(LOCAL_KEY, '1');
    const tl = gsap.timeline({ onComplete: () => setVisible(false) });
    tl.to(cardRef.current, { scale: 0.92, opacity: 0, y: 20, duration: 0.3 });
    tl.to(backdropRef.current, { opacity: 0, duration: 0.25 }, '<0.05');
  };

  if (!visible || !event) return null;

  return (
    <div className="popup_backdrop" ref={backdropRef} onClick={(e) => e.target === backdropRef.current && dismiss()}>
      <div className="popup_card" ref={cardRef}>
        <button className="popup_close" onClick={dismiss}>✕</button>
        <div className="popup_inner">
          
          <div className="popup_badge"><span className="popup_badge_dot" /><p>Booking Open</p></div>
          <h2 className="popup_event_name">{event.event_name}</h2>
          
          <div className="popup_meta">
            <div className="popup_meta_row">
              <div className="popup_meta_icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#96B5C9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
              </div>
              <div>
                <p className="popup_meta_label">Event Dates</p>
                <p className="popup_meta_value">{fmt(event.event_start_date)} – {fmt(event.event_end_date)}</p>
              </div>
            </div>

            <div className="popup_meta_row">
              <div className="popup_meta_icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#96B5C9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
              </div>
              <div>
                <p className="popup_meta_label">Booking Closes</p>
                <p className="popup_meta_value">{fmt(event.booking_end_date)}</p>
              </div>
            </div>

            {event.available_days && (
              <div className="popup_meta_row">
                <div className="popup_meta_icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#96B5C9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="8" y1="14" x2="16" y2="14" /></svg>
                </div>
                <div>
                  <p className="popup_meta_label">Available Days</p>
                  <div className="popup_days">
                    {event.available_days.map((d, i) => <span className="popup_day_chip" key={i}>{d}</span>)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="popup_divider" />

          {!countdown.expired && (
            <div>
              <p className="popup_countdown_label">Booking closes in</p>
              <div className="popup_countdown">
                {[{ n: countdown.days, l: 'Days' }, { n: countdown.hours, l: 'Hrs' }, { n: countdown.mins, l: 'Mins' }, { n: countdown.secs, l: 'Secs' }].map((unit, i) => (
                  <div className="popup_countdown_unit" key={i}>
                    <p className="popup_countdown_num">{String(unit.n).padStart(2, '0')}</p>
                    <p>{unit.l}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <a href="/booking" className="popup_cta" onClick={dismiss}>
            <span>Book Your Spot</span>
            <span className="popup_cta_arrow">→</span>
          </a>

        </div>
      </div>
    </div>
  );
};

export default PopupAd;
